"use client"

import { useState, useEffect, useMemo, useRef } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { Package, ShoppingCart, BarChart2, Bell, BellRing, CalendarClock, Search, Settings, Lock, AlertTriangle, PackageX, CheckCircle2, Truck, BookOpen, HandCoins, Users, ScanLine, Hammer, Globe } from 'lucide-react'
// framer-motion removed 2026-09-06: this component renders on EVERY logged-in
// page (it lives in app/(app)/layout.tsx), so the library was evaluated in the
// Worker's cold start and pushed it past the 10 ms CPU limit — Error 1102.
// The entrances are CSS keyframes in globals.css; the browser pays for them.
import { cn } from '@/lib/utils'
import { useStore } from '@/lib/store'
import { ownerWindow } from '@/lib/expiry'
import { windowLine } from '@/components/expiry-window'
import { LOW_STOCK } from '@/lib/intelligence'
import { stockProducts } from '@/lib/service'
import { costTitle, dayLabel, tbilisiToday, waitingForAmount } from '@/lib/recurring-expenses'
import { useNow } from '@/hooks/use-now'
import { ShiftControl } from '@/components/shifts/shift-control'
import { ThemeToggleCompact } from '@/components/theme-toggle'
import { BranchSwitcher } from '@/components/branch-switcher'
import { SupportPill } from '@/components/support-alerts'
import { longDateText, useLang, useT, type Key, type Lang } from '@/lib/i18n'

/** Rows the bell's list draws; the warehouse holds the rest. */
const NOTIF_SHOWN = 50

const MODULE_MAP: Record<string, { label: Key; icon: React.ElementType; gradient: string; sub: Key }> = {
  '/warehouse': { label: 'nav.warehouse',  icon: Package,      gradient: 'from-blue-500 to-indigo-600',   sub: 'module.warehouse.sub' },
  '/pos':       { label: 'nav.pos',        icon: ShoppingCart, gradient: 'from-violet-500 to-purple-600', sub: 'module.pos.sub' },
  '/suppliers': { label: 'nav.suppliers',  icon: Truck,        gradient: 'from-cyan-500 to-blue-600',     sub: 'module.suppliers.sub' },
  '/credit':    { label: 'nav.credit',     icon: HandCoins,    gradient: 'from-amber-500 to-orange-600',  sub: 'module.credit.sub' },
  '/accounting':{ label: 'nav.accounting', icon: BarChart2,    gradient: 'from-emerald-500 to-teal-600',  sub: 'module.accounting.sub' },
  '/guide':     { label: 'nav.guide',      icon: BookOpen,     gradient: 'from-pink-500 to-rose-600',     sub: 'module.guide.sub' },
  '/staff':     { label: 'nav.staff',      icon: Users,        gradient: 'from-fuchsia-500 to-pink-600',  sub: 'module.staff.sub' },
  '/settings':  { label: 'nav.settings',   icon: Settings,     gradient: 'from-slate-500 to-slate-700',   sub: 'module.settings.sub' },
  '/repairs':   { label: 'nav.repairs',    icon: Hammer,       gradient: 'from-lime-500 to-green-600',    sub: 'module.repairs.sub' },
  '/orders':    { label: 'nav.orders',     icon: Globe,        gradient: 'from-sky-500 to-blue-600',      sub: 'module.orders.sub' },
  '/terminal':  { label: 'nav.terminal',   icon: ScanLine,     gradient: 'from-teal-500 to-cyan-600',     sub: 'terminal.detail' },
}

// Georgian says its date its own way; every other language asks Intl.
function longDate(lang: Lang, d: Date): string {
  return longDateText(d, { weekday: true, year: true }, lang)
}

export function TopHeader() {
  const pathname = usePathname()
  const key = Object.keys(MODULE_MAP).find(k => pathname.startsWith(k)) ?? '/warehouse'
  const { label, icon: Icon, gradient, sub } = MODULE_MAP[key]
  const { lock, products } = useStore()
  const t = useT()
  const lang = useLang()
  const router = useRouter()

  const [dateLabel, setDateLabel] = useState<string | null>(null)
  const [notifOpen, setNotifOpen] = useState(false)
  const notifRef = useRef<HTMLDivElement>(null)
  const [quickSearch, setQuickSearch] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const setGlobalSearch = useStore((s) => s.setGlobalSearch)

  // ⌘K / Ctrl+K focuses the quick-search box.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key?.toLowerCase() === 'k') {   // autofill's keydown has no key
        e.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Enter hands the query to the warehouse product list.
  const submitQuickSearch = () => {
    const q = quickSearch.trim()
    if (!q) return
    setGlobalSearch(q)
    setQuickSearch('')
    router.push('/warehouse')
  }

  // Real notifications: out-of-stock first, then low-stock (<= 5).
  // Memoised: this header re-renders on every keystroke of its quick search,
  // and a 30 000-product chain filtered and sorted the catalogue each time.
  const lowStock = useMemo(() => stockProducts(products)   // 169 — a service is never low
    // Same rule as everywhere else — see lib/intelligence.ts.
    .filter(p => p.quantity > 0 && p.quantity <= LOW_STOCK)
    .sort((a, b) => a.quantity - b.quantity), [products])
  // 121 — a monthly cost whose amount changes, waiting on its day. The owner
  // only: he is the one who can answer it.
  const now = useNow(5 * 60_000)
  const recurring = useStore((s) => s.recurringExpenses)
  const isOwner = useStore((s) => s.userRole === 'owner' && !s.supportSession)
  const dueCosts = isOwner ? waitingForAmount(recurring, tbilisiToday(now)) : []
  // 1.61 — the batches expired or expiring within 7 days: one entry, the owner's, while there are any
  const stockLots = useStore((s) => s.stockLots)
  const expiryW = useMemo(() => (isOwner ? ownerWindow(stockLots, products) : null), [isOwner, stockLots, products])
  const notifCount = lowStock.length + dueCosts.length + (expiryW ? 1 : 0)

  // Close dropdown on outside click
  useEffect(() => {
    if (!notifOpen) return
    const onClick = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [notifOpen])

  useEffect(() => {
    setDateLabel(longDate(lang, new Date()))
  }, [lang])

  // 121 — a due cost's words, in the interface language (lib/recurring-expenses).
  const dueTitle = (r: (typeof dueCosts)[number]) => costTitle(r.category, r.next_due)
  const dueDay = (r: (typeof dueCosts)[number]) => dayLabel(r.next_due)

  return (
    <header
      className="app-shell-surface animate-fade-down h-14 md:h-16 floating-panel rounded-3xl shrink-0 z-20 flex items-center justify-between px-4 md:px-6 gap-2 md:gap-4"
    >

      {/* Left: module title. The mobile hamburger was removed — the bottom
          tab bar's "მეტი" opens the same drawer (one control, one home).
          Phones (2026-09-11): a large title, the platform's own way of saying
          "where am I" — the coloured module tile was spending 44px of a 358px
          row to repeat what the tab capsule already shows, and pushed the
          name into „პარამეტ…". */}
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        {/* layoutId (shared-element morph between modules) went with
            framer-motion. A keyed re-entrance reads almost the same and costs
            nothing on the server. */}
        <div
          key={key}
          className={cn('animate-scale-in hidden md:flex size-9 rounded-xl bg-gradient-to-br items-center justify-center shadow-md shrink-0', gradient)}
        >
          <Icon className="size-4.5 text-white" />
        </div>
        <div className="min-w-0">
          <h1 className="text-[20px] md:text-sm font-black md:font-bold tracking-[-0.02em] md:tracking-normal text-foreground leading-tight truncate">{t(label)}</h1>
          <p className="hidden sm:block text-[12px] text-muted-foreground leading-none mt-0.5 truncate">{t(sub)}</p>
        </div>
      </div>

      {/* Center: search bar */}
      <div className="hidden md:flex flex-1 max-w-xs">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
          <input
            ref={searchRef}
            type="text"
            placeholder={t('header.quickSearch')}
            value={quickSearch}
            onChange={(e) => setQuickSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitQuickSearch() }}
            className="w-full h-9 pl-9 pr-4 text-base sm:text-sm bg-muted border border-transparent rounded-xl outline-none focus:border-primary/40 focus:bg-card dark:focus:bg-white/10 focus:shadow-sm focus:shadow-primary/10 transition-all placeholder:text-muted-foreground/60"
          />
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden lg:flex items-center gap-0.5 h-5 px-1.5 rounded-md bg-border text-[12px] text-muted-foreground font-mono">
            ⌘K
          </kbd>
        </div>
      </div>

      {/* Right: branch + shift + date + bell + avatar */}
      <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 shrink-0">
        {/* 1.45 — the platform admin only, while a shop waits for help */}
        <SupportPill />
        {/* renders nothing until the org has >1 branch */}
        <BranchSwitcher />
        <ShiftControl />

        {dateLabel && (
          <span className="hidden lg:block text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-lg border border-border">
            {dateLabel}
          </span>
        )}

        {/* Phones: the theme lives in „მეტი" instead. With four controls up
            here the page title kept losing its last letters („ბუღალტერ…"),
            and the theme is changed once, not daily. One home per layout. */}
        <ThemeToggleCompact className="hidden md:flex" />

        {/* Lock — hidden on mobile; same action is reachable via the sidebar drawer footer */}
        <button
          onClick={lock}
          title={t('shell.lockSystem')}
          className="hidden md:flex size-9 rounded-xl border border-border bg-card dark:bg-white/5 hover:bg-red-50 dark:bg-red-500/10 dark:hover:bg-red-500/10 hover:border-red-200 dark:border-red-500/25 dark:hover:border-red-500/30 transition-all duration-150 items-center justify-center group"
        >
          <Lock className="size-4 text-muted-foreground group-hover:text-red-500 transition-colors" />
        </button>

        {/* Bell + notifications dropdown */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => setNotifOpen(o => !o)}
            title={t('notif.title')}
            className={cn(
              "relative size-9 rounded-full md:rounded-xl border transition-colors duration-150 flex items-center justify-center group active:scale-[0.94] [-webkit-tap-highlight-color:transparent]",
              notifOpen ? "bg-accent border-accent" : "border-border bg-card dark:bg-white/5 hover:bg-accent hover:border-accent"
            )}
          >
            <Bell className="size-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            {notifCount > 0 && (
              // 99+ — a 30 000-product chain had 8 412, wider than the bell
              // (scale test, 2026-09-30); the list inside says how many.
              <span className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1.5 rounded-full bg-red-500 text-white text-[12px] font-bold flex items-center justify-center tabular-nums">
                {notifCount > 99 ? '99+' : notifCount}
              </span>
            )}
          </button>

          {/* No exit animation any more — a dropdown that vanishes on click
              reads as responsive, and keeping AnimatePresence for it was the
              only reason this file needed the library at runtime. */}
          {notifOpen && (
            <div
              // Fixed + inset-x on mobile so the panel spans the viewport with a safe
              // margin instead of anchoring to the bell icon — at narrow widths the
              // bell isn't the header's rightmost control, so a right-0 anchored
              // w-80 panel could hang off the left edge of the screen.
              className="animate-pop-down fixed inset-x-3 top-[4.75rem] sm:absolute sm:inset-x-auto sm:top-12 sm:right-0 z-50 w-auto sm:w-80 max-h-[70dvh] bg-card/90 dark:bg-[oklch(0.24_0.016_258/0.95)] backdrop-blur-xl rounded-2xl border border-border shadow-xl shadow-black/10 dark:shadow-black/40 overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center gap-2.5 px-4 py-3.5 border-b border-border bg-gradient-to-r from-amber-500/5 to-orange-500/5">
                <div className="size-8 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                  <AlertTriangle className="size-4 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-foreground leading-tight">{dueCosts.length > 0 ? t('notif.title') : t('notif.stockTitle')}</p>
                  <p className="text-[12px] text-muted-foreground leading-none mt-0.5">{dueCosts.length > 0 ? t('notif.subBoth') : t('notif.subStock')}</p>
                </div>
                {notifCount > 0 && (
                  <span className="text-[12px] font-bold px-2 py-0.5 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-500/20">{notifCount}</span>
                )}
              </div>

              {/* List */}
              <div className="overflow-y-auto flex-1">
                {/* 121 — first: they are money, and they wait for an answer */}
                {dueCosts.map(r => (
                  <button
                    key={r.id}
                    onClick={() => { setNotifOpen(false); router.push('/accounting?tab=expenses') }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/60 transition-colors text-left border-b border-border/40"
                  >
                    <div className="size-9 rounded-xl flex items-center justify-center shrink-0 bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400">
                      <BellRing className="size-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">
                        {dueTitle(r)}{r.note ? ` · ${r.note}` : ''}
                      </p>
                      <p className="text-[12px] font-medium text-amber-600 dark:text-amber-400">
                        {t('notif.waitingAmount', { day: dueDay(r) })}
                      </p>
                    </div>
                  </button>
                ))}
                {expiryW && (
                  <button
                    onClick={() => { setNotifOpen(false); router.push('/accounting?tab=doctor&doctor=expiry') }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/60 transition-colors text-left border-b border-border/40"
                  >
                    <div className={cn('size-9 rounded-xl flex items-center justify-center shrink-0',
                      expiryW.expired > 0 ? 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400' : 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400')}>
                      <CalendarClock className="size-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{t('acc.lot.bell', { n: expiryW.expired + expiryW.soon })}</p>
                      <p className="text-[12px] font-medium text-muted-foreground truncate">
                        {windowLine(expiryW, t)}
                      </p>
                    </div>
                  </button>
                )}
                {lowStock.length === 0 && (dueCosts.length > 0 || expiryW) ? null : lowStock.length === 0 ? (
                  <div className="flex flex-col items-center gap-2.5 py-10 text-muted-foreground">
                    <CheckCircle2 className="size-8 text-emerald-400" />
                    <p className="text-sm font-medium">{t('notif.allGood')}</p>
                  </div>
                ) : (
                  // The first NOTIF_SHOWN — the scale test's chain had 8 412, and a
                  // phone drew every one of them when the bell was opened.
                  lowStock.slice(0, NOTIF_SHOWN).map(p => {
                    const out = p.quantity === 0
                    return (
                      <button
                        key={p.id}
                        onClick={() => { setNotifOpen(false); router.push('/warehouse') }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/60 transition-colors text-left border-b border-border/40 last:border-0"
                      >
                        <div className={cn(
                          "size-9 rounded-xl flex items-center justify-center shrink-0",
                          out ? "bg-red-50 dark:bg-red-500/10 text-red-500" : "bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400"
                        )}>
                          {out ? <PackageX className="size-4" /> : <Package className="size-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground truncate">{p.name}</p>
                          <p className={cn("text-[12px] font-medium", out ? "text-red-500" : "text-amber-600 dark:text-amber-400")}>
                            {out ? t('notif.outOfStock') : t('notif.left', { n: p.quantity })}
                          </p>
                        </div>
                      </button>
                    )
                  })
                )}
                {lowStock.length > NOTIF_SHOWN && (
                  <p className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground">
                    {t('notif.more', { n: lowStock.length - NOTIF_SHOWN })}
                  </p>
                )}
              </div>

              {/* Footer */}
              {lowStock.length > 0 && (
                <button
                  onClick={() => { setNotifOpen(false); router.push('/warehouse') }}
                  className="px-4 py-3 border-t border-border text-center text-xs font-bold text-primary hover:bg-primary/5 transition-colors"
                >
                  {t('notif.goWarehouse')}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Sign out lives in the sidebar footer only — it sits next to the
            user's identity there and stays reachable on phones (this header
            button used to clip off-screen on narrow widths). The decorative
            "AD" avatar that used to sit here was removed too: it did nothing
            and duplicated the sidebar-footer identity block. */}
      </div>
    </header>
  )
}
